import { useMemo } from "react";
import {
  scorePassword,
  checkRequirements,
  getStrengthLabel,
  getStrengthColor,
  meetsMinStrength,
} from "../lib/password-utils";

/**
 * Advisory password strength meter + requirements checklist.
 *
 * Purely visual guidance — it does NOT block submit on its own. The actual
 * minimum-strength gate is enforced in each form's submit handler (and on the
 * backend) via meetsMinStrength() from the same shared module.
 */
export default function PasswordStrength({ password = "", showRequirements = true }) {
  const score = useMemo(() => scorePassword(password), [password]);
  const requirements = useMemo(() => checkRequirements(password), [password]);

  if (!password) return null;

  const color = getStrengthColor(password);
  const ok = meetsMinStrength(password);

  return (
    <div style={{ marginTop: 8 }}>
      {/* Strength bar — 4 segments filled up to the score */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ display: "flex", gap: 4, flex: 1 }}>
          {[1, 2, 3, 4].map((i) => (
            <span
              key={i}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                background: i <= score ? color : "var(--border, #e5e7eb)",
                transition: "background 0.2s",
              }}
            />
          ))}
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, color, minWidth: 56, textAlign: "right" }}>
          {getStrengthLabel(password)}
        </span>
      </div>

      {/* Requirements checklist (advisory) */}
      {showRequirements && (
        <ul
          style={{
            listStyle: "none",
            margin: "8px 0 0",
            padding: 0,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "2px 12px",
          }}
        >
          {requirements.map((req) => (
            <li
              key={req.key}
              style={{
                fontSize: 11,
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: req.met ? "#22c55e" : "var(--text-muted, #9ca3af)",
              }}
            >
              <span>{req.met ? "✓" : "○"}</span>
              <span>{req.label}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Gate hint — only when below the acceptable minimum */}
      {!ok && (
        <div style={{ fontSize: 11, color: "var(--text-muted, #9ca3af)", marginTop: 6 }}>
          Tip: longer passphrases are stronger than short complex ones.
        </div>
      )}
    </div>
  );
}
